// Boot-time config loader. Reads /config.json (served from public/) once and
// hands the parsed object to the rest of the app via React context.
//
// Modeled on Workbench's editorConfig pattern (origin/main:src/config/defaults.js
// + components/AppConfigProvider). Kept minimal: one fetch at boot, one provider,
// one hook. No async render orchestration beyond a loading splash.
//
// VITE_APP_CONFIG_URL overrides the default path for tests / preview deploys.

import { createContext, useContext, useEffect, useState } from 'react'

const DEFAULT_CONFIG_URL = '/config.json'

// Used as a fallback when /config.json is missing in dev (Vite serves nothing
// from public/ if you delete the file). Keeps the app bootable; logs a warning.
const FALLBACK_CONFIG = {
  auth: {
    mode: 'oauth',
    // issuer = the Bitol AS. resource = this webapp's RFC 8707 resource
    // identifier, registered in Bitol's signin-clients/next-webapp.yaml.
    // It is deliberately NOT api.jgp.ai (that URL is owned by workbench.yaml;
    // resource URLs must be unique across signin-client files).
    issuer: 'https://api.jgp.ai',
    resource: 'https://next.actianlabs.com',
    scope: 'email',
  },
}

const AppConfigContext = createContext(null)

export async function loadAppConfig() {
  const url = import.meta.env?.VITE_APP_CONFIG_URL || DEFAULT_CONFIG_URL
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const cfg = await res.json()
    return mergeWithFallback(cfg)
  } catch (err) {
    console.warn('[appConfig] /config.json load failed, using fallback:', err?.message)
    return FALLBACK_CONFIG
  }
}

function mergeWithFallback(cfg) {
  return {
    ...FALLBACK_CONFIG,
    ...cfg,
    auth: { ...FALLBACK_CONFIG.auth, ...(cfg?.auth || {}) },
  }
}

export function AppConfigProvider({ children }) {
  const [config, setConfig] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadAppConfig().then((cfg) => { if (!cancelled) setConfig(cfg) })
    return () => { cancelled = true }
  }, [])

  if (!config) return null  // tiny boot gap; replace with splash if it ever becomes visible
  return <AppConfigContext.Provider value={config}>{children}</AppConfigContext.Provider>
}

export function useAppConfig() {
  const ctx = useContext(AppConfigContext)
  // Return the in-process fallback when there's no provider — keeps unit
  // tests bootable without forcing them all to wrap in AppConfigProvider.
  return ctx || FALLBACK_CONFIG
}

export function useAuthConfig() {
  return useAppConfig().auth
}
